import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TurmaProfile from "../Turma/TurmaProfile";
import TurmaTableSelect from "../Turma/TurmaTableSelect";
import MuralFotoLightbox from "../Mural/MuralFoto/MuralFotoLightbox";
import Container from "../UI/Container";
import Loading from "../UI/Loading";
import AlunoFotoList from "./AlunoFotoList";
import { useAlunoFotos } from "./hooks/useAlunoFotos";

const TODOS = "todos";
const COM_FOTO = "com-foto";
const SEM_FOTO = "sem-foto";

const normalize = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const AlunoFotoPorTurmaContainer = () => {
  const [turma, setTurma] = useState(null);
  const { listaAF, isLoading } = useAlunoFotos(turma);
  const [filtro, setFiltro] = useState(TODOS);
  const [busca, setBusca] = useState("");
  const [fotoIndex, setFotoIndex] = useState(null);

  const totalComFoto = listaAF.filter((af) => af.foto).length;
  const totalSemFoto = listaAF.length - totalComFoto;

  const visiveis = useMemo(() => {
    const termo = normalize(busca.trim());
    return listaAF.filter((af) => {
      if (filtro === COM_FOTO && !af.foto) return false;
      if (filtro === SEM_FOTO && af.foto) return false;
      if (!termo) return true;
      return normalize(af.aluno?.nome).includes(termo) || String(af.aluno?.codAlunoEscola ?? "").includes(termo);
    });
  }, [listaAF, filtro, busca]);

  const comFoto = visiveis.filter((af) => af.foto);
  // A grade usa a posição na lista visível; o visualizador só recebe fotos com imagem.
  const handleOpenFoto = (index) => setFotoIndex(comFoto.indexOf(visiveis[index]));

  const handleTrocaTurma = () => {
    setTurma(null);
    setFiltro(TODOS);
    setBusca("");
  };

  const renderFotos = () => {
    if (isLoading) {
      return (
        <Box py={6}>
          <Loading description="Carregando fotos..." />
        </Box>
      );
    }
    if (listaAF.length === 0) {
      return (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            color: "text.secondary",
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <PhotoLibraryOutlinedIcon fontSize="large" />
          <Typography variant="body2" mt={1}>
            Nenhum aluno encontrado nesta turma.
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} mb={2} alignItems={{ md: "center" }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filtro}
            onChange={(event, value) => value && setFiltro(value)}
          >
            <ToggleButton value={TODOS}>Todos ({listaAF.length})</ToggleButton>
            <ToggleButton value={COM_FOTO}>Com foto ({totalComFoto})</ToggleButton>
            <ToggleButton value={SEM_FOTO}>Sem foto ({totalSemFoto})</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            placeholder="Buscar aluno por nome ou código"
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            sx={{ flexGrow: 1, maxWidth: { md: 360 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>

        {visiveis.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
            Nenhum aluno corresponde ao filtro.
          </Typography>
        ) : (
          <AlunoFotoList listaAF={visiveis} showAluno showAno={false} onOpenFoto={handleOpenFoto} />
        )}
      </>
    );
  };

  return (
    <Container>
      <MuralFotoLightbox
        fotos={comFoto}
        index={fotoIndex}
        onClose={() => setFotoIndex(null)}
        onChangeIndex={setFotoIndex}
      />

      <Box mb={3}>
        <Typography variant="h6">Aluno Foto por Turma</Typography>
        <Typography variant="body2" color="text.secondary">
          {turma ? "Fotos dos alunos da turma selecionada." : "Selecione uma turma para ver as fotos dos alunos."}
        </Typography>
      </Box>

      {turma ? (
        <Stack spacing={3}>
          <TurmaProfile turma={turma} handleTrocaTurma={handleTrocaTurma} />
          <Box>{renderFotos()}</Box>
        </Stack>
      ) : (
        <TurmaTableSelect onTurmaSelected={setTurma} />
      )}
    </Container>
  );
};

export default AlunoFotoPorTurmaContainer;
